import { useState, useCallback } from "react";
import { useTenant } from "../../../../hooks/useTenant";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useCashierBanks } from "./useCashierBanks";
import { useCashierShifts } from "./useCashierShifts";
import { useCashierRecords } from "./useCashierRecords";

const today = () => {
    // Usa timezone nativo — DST-safe (não depende de offset fixo -3h)
    const parts = new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" }).split("-");
    return `${parts[0]}-${parts[1].padStart(2, "0")}-${parts[2].padStart(2, "0")}`;
};

/**
 * Hook para a tela de Caixa Diário.
 * Refatorado seguindo S.O.L.I.D. - Agora orquestra sub-hooks de responsabilidade única.
 */
export const useCashier = () => {
    const { idTenant, user } = useTenant();
    const { can, isFullAccess } = usePermissions();
    const canViewAllCashier = isFullAccess || can("financial_cashflow") || can("financial_config");

    // Controle de Filtros e UI
    const [selectedDate, setSelectedDate] = useState(today());
    const [modals, setModals] = useState({ open: false, close: false });

    // Funções controladoras de Modal
    const toggleOpenModal = useCallback(() => setModals(prev => ({ ...prev, open: !prev.open })), []);
    const toggleCloseModal = useCallback(() => setModals(prev => ({ ...prev, close: !prev.close })), []);

    // 1. Hook de Bancos
    const { bankAccounts, loadingBanks } = useCashierBanks(idTenant);

    // 2. Hook de Turnos
    const { 
        availableShifts, currentShift, isProcessingShift,
        handleSelectShift, handleOpenShift, handleCloseShift 
    } = useCashierShifts(idTenant, user, selectedDate, canViewAllCashier);

    // 3. Hook de Registros e Balanço
    const { 
        records, summary, loading, error, refresh 
    } = useCashierRecords(idTenant, user, selectedDate, currentShift, canViewAllCashier);

    // Encapsula execuções conectando o Turno com o Refresh da tabela
    const onConfirmOpen = useCallback((data) => {
        handleOpenShift(data, toggleOpenModal);
    }, [handleOpenShift, toggleOpenModal]);

    const onConfirmClose = useCallback((data) => {
        handleCloseShift(data, summary, () => {
            toggleCloseModal();
            refresh();
        });
    }, [handleCloseShift, summary, toggleCloseModal, refresh]);

    return {
        selectedDate,
        setSelectedDate,
        bankAccounts,
        loadingBanks,
        availableShifts,
        canViewAllCashier,
        records,
        summary,
        loading,
        error,
        refresh,

        currentShift,
        modals,
        toggleOpenModal,
        toggleCloseModal,
        isProcessingShift,
        handleSelectShift,
        handleOpenShift: onConfirmOpen,
        handleCloseShift: onConfirmClose
    };
};

